import type { UIStateAPI } from "@/types";
import {
  zSelectedMode,
  zSelectedTool,
  zSketchEraserMode,
  zSketchSelectionMode,
} from "@/data/schemas";
import {
  ensureSubtoolForMode,
  getModeForPrimaryTool,
  getModeForSubtool,
  getSubtoolsForMode,
  getPrimaryToolForMode,
} from "@/data/tool-mode-map";

const resolvePrimaryToolForMode = (
  mode: string | null | undefined
): string | null => {
  if (!mode) return null;
  const subtools = getSubtoolsForMode(mode as any);
  if (!subtools.length) return null;
  return getPrimaryToolForMode(mode as any) ?? null;
};

interface SelectionAdapterDeps {
  readonly store: any;
  readonly uiState: UIStateAPI;
  readonly eventBus: any;
  readonly getPath: (key: string) => string | null;
  readonly persist: () => void;
}

/**
 * Registers selection (selectedMode, selectedTool) sync between store and UI state.
 * Returns an unsubscribe function to remove all listeners.
 */
export const registerSelectionAdapter = (
  deps: SelectionAdapterDeps
): (() => void) => {
  const { store, uiState, eventBus, getPath, persist } = deps;

  const unsubscribes: (() => void)[] = [];
  let isApplyingFromStore = false;

  // Debug support (toggle via localStorage['debug.adapters'] or event 'debug:adapter')
  const readDebugEnabled = (): boolean => {
    try {
      const d =
        (globalThis as any)?.localStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.sessionStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.__debugAdapters ||
        "";
      if (typeof d !== "string") return !!(d as any)?.selection || d === "*";
      const parts = d.split(",").map((s: string) => s.trim());
      return parts.includes("*") || parts.includes("selection");
    } catch {
      return false;
    }
  };
  let debugEnabled = readDebugEnabled();
  const debug = (...args: unknown[]) => {
    if (debugEnabled) {
      try {
        // eslint-disable-next-line no-console
        console.debug("[adapter:selection]", ...args);
      } catch {}
    }
  };
  try {
    const unsubDbg = eventBus.on(
      "debug:adapter",
      (payload: { name?: string; enabled?: boolean } | undefined) => {
        try {
          if (!payload) return;
          if (payload.name === "selection") debugEnabled = !!payload.enabled;
          if (payload.name === "*" && typeof payload.enabled === "boolean")
            debugEnabled = !!payload.enabled;
        } catch {}
      }
    );
    unsubscribes.push(unsubDbg);
  } catch {}

  // Store -> UIState: selectedMode
  const unsubscribeStoreSelectedMode = store.subscribe(
    (path: string) => path.endsWith(".screenState.selectedMode"),
    ({ value, source }: { value: unknown; source?: string }) => {
      const isHydration = source === "hydration";
      let incomingMode: string | null = null;
      let uiSubtool: string | null | undefined;
      try {
        if (typeof value === "string") {
          incomingMode = value;
          isApplyingFromStore = true;
          try {
            (uiState as any).setMode(value as any);
            const snapshot = uiState.getState?.();
            uiSubtool = snapshot?.selectedTool ?? null;
            debug("store→UI setMode", {
              value,
              source,
              resolvedSubtool: uiSubtool,
            });
          } finally {
            isApplyingFromStore = false;
          }
        }
      } catch {}

      if (incomingMode) {
        const desiredSubtool =
          typeof uiSubtool === "string" ? uiSubtool : null;

        const subtoolPath = getPath("selectedTool");
        if (subtoolPath) {
          const current = store.get?.(subtoolPath) as
            | string
            | null
            | undefined;
          const normalized = ensureSubtoolForMode(
            incomingMode as any,
            desiredSubtool ?? current ?? null
          );
          const shouldUpdate =
            normalized !== current &&
            !(normalized === null && (current === null || typeof current === "undefined"));
        const writeSource = isHydration
          ? "hydration"
          : source === "socket"
          ? "socket"
          : "selectionAdapter";
        if (shouldUpdate) {
          store.set(subtoolPath, normalized as any, {
            source: writeSource,
          });
          const legacySubtoolPath = getPath("selected_subtool");
          if (legacySubtoolPath) {
            store.set(legacySubtoolPath, normalized as any, {
              source: writeSource,
            });
          }
        }
        }

        const primaryToolPath = getPath("selected_tool");
        if (primaryToolPath) {
          const desiredTool = resolvePrimaryToolForMode(incomingMode);
          const currentPrimary = store.get?.(primaryToolPath);
          if (currentPrimary !== desiredTool) {
            store.set(primaryToolPath, desiredTool as any, {
              source: writeSource,
            });
          }
        }
      }

      if (!isHydration && source !== "socket") persist();
    },
    { debounceMs: 50 }
  );
  unsubscribes.push(unsubscribeStoreSelectedMode);

  const unsubscribeSketchEraserMode = store.subscribe(
    (path: string) => path.endsWith(".screenState.sketchEraserMode"),
    ({ value }: { value: unknown }) => {
      if (value !== "delete" && value !== "delete-clean") return;
      try {
        uiState.applyState({ sketchEraserMode: value } as any);
      } catch {}
    },
    { debounceMs: 50 }
  );
  unsubscribes.push(unsubscribeSketchEraserMode);

  const unsubscribeSketchSelectionMode = store.subscribe(
    (path: string) => path.endsWith(".screenState.sketchSelectionMode"),
    ({ value }: { value: unknown }) => {
      if (value !== "marquee" && value !== "lasso") return;
      try {
        uiState.applyState({ sketchSelectionMode: value } as any);
      } catch {}
    },
    { debounceMs: 50 }
  );
  unsubscribes.push(unsubscribeSketchSelectionMode);

  // Store -> UIState: selectedTool
  const unsubscribeStoreSelectedTool = store.subscribe(
    (path: string) => path.endsWith(".screenState.selectedTool"),
    ({ value, source }: { value: unknown; source?: string }) => {
      const isHydration = source === "hydration";
      const incoming = typeof value === "string" ? value : null;
      let snapshotMode: string | null | undefined;
      let snapshotSubtool: string | null | undefined;
      try {
        isApplyingFromStore = true;
        try {
          (uiState as any).setToolSub(incoming as any);
          const snapshot = uiState.getState?.();
          snapshotMode = snapshot?.selectedMode ?? null;
          snapshotSubtool = snapshot?.selectedTool ?? null;
          debug("store→UI setToolSub", {
            value,
            source,
            resolvedSubtool: snapshotSubtool,
          });
        } finally {
          isApplyingFromStore = false;
        }
      } catch {}

      let effectiveMode =
        typeof snapshotMode === "string"
          ? (snapshotMode as string)
          : null;
      if (!effectiveMode) {
        const inferred = getModeForSubtool(snapshotSubtool ?? incoming);
        if (inferred) effectiveMode = inferred;
      }

      const hasSubtools =
        !!effectiveMode &&
        getSubtoolsForMode(effectiveMode as any).length > 0;

      const writeSource = isHydration
        ? "hydration"
        : source === "socket"
        ? "socket"
        : "selectionAdapter";

      if (effectiveMode && hasSubtools) {
        const modePath = getPath("selectedMode");
        if (modePath) {
          const currentModeValue = store.get?.(modePath);
          if (currentModeValue !== effectiveMode) {
            store.set(modePath, effectiveMode, {
              source: writeSource,
            });
          }
        }
      }

      const normalized = ensureSubtoolForMode(
        (effectiveMode as any) || null,
        snapshotSubtool ?? incoming
      );
      const finalValue = normalized ?? null;
      if (finalValue !== incoming) {
        store.set(path, finalValue as any, {
          source: writeSource,
        });
      }

      const legacySubtoolPath = getPath("selected_subtool");
      if (legacySubtoolPath) {
        const currentLegacy = store.get?.(legacySubtoolPath);
        if (currentLegacy !== finalValue) {
          store.set(legacySubtoolPath, finalValue as any, {
            source: writeSource,
          });
        }
      }

      const primaryToolPath = getPath("selected_tool");
      if (primaryToolPath) {
        const desiredTool = resolvePrimaryToolForMode(effectiveMode);
        const currentPrimary = store.get?.(primaryToolPath);
        if (currentPrimary !== desiredTool) {
          store.set(primaryToolPath, desiredTool as any, {
            source: writeSource,
          });
        }
      }

      if (!isHydration && source !== "socket") persist();
    },
    { debounceMs: 50 }
  );
  unsubscribes.push(unsubscribeStoreSelectedTool);

  // UIState -> Store: listen to tool changes via event bus
  const unsubscribeToolChanged = eventBus.on(
    "tool-changed",
    (payload: {
      tool: string;
      subtool: string | null;
      mode?: string | null;
    }) => {
      if (!payload) return;
      if (isApplyingFromStore) return; // avoid echo loops
      const { tool, subtool, mode: modeFromEvent } = payload;
      const primaryMode =
        (typeof modeFromEvent === "string" && modeFromEvent) ||
        getModeForPrimaryTool(tool as any);
      const mode =
        (primaryMode as any) ||
        (tool === ("draw" as any) ? ("sketch" as any) : (tool as any));

      const mPath = getPath("selectedMode");
      const tPath = getPath("selectedTool");
      if (!mPath || !tPath) return;

      try {
        try {
          store.registerSchema(mPath, zSelectedMode);
        } catch {}
        try {
          store.registerSchema(tPath, zSelectedTool);
        } catch {}
        const eraserModePath = getPath("sketchEraserMode");
        if (eraserModePath) {
          try {
            store.registerSchema(eraserModePath, zSketchEraserMode);
          } catch {}
        }
        const selectionModePath = getPath("sketchSelectionMode");
        if (selectionModePath) {
          try {
            store.registerSchema(selectionModePath, zSketchSelectionMode);
          } catch {}
        }
        const legacySubtoolPath = getPath("selected_subtool");
        if (legacySubtoolPath) {
          try {
            store.registerSchema(legacySubtoolPath, zSelectedTool);
          } catch {}
        }
        debug("UI→store selection", { mode, subtool });
        store.set(mPath, mode as any, { source: "selectionAdapter" });
        if (typeof subtool === "string") {
          store.set(tPath, subtool as any, { source: "selectionAdapter" });
          if (legacySubtoolPath) {
            store.set(legacySubtoolPath, subtool as any, {
              source: "selectionAdapter",
            });
          }
        } else {
          store.set(tPath, undefined, { source: "selectionAdapter" });
          if (legacySubtoolPath) {
            store.set(legacySubtoolPath, undefined, {
              source: "selectionAdapter",
            });
          }
        }
      const primaryToolPath = getPath("selected_tool");
      if (primaryToolPath) {
        const desiredTool = resolvePrimaryToolForMode(mode);
        store.set(primaryToolPath, desiredTool as any, {
          source: "selectionAdapter",
        });
      }
      } catch {}
    }
  );
  unsubscribes.push(unsubscribeToolChanged);

  const cleanup = () => {
    debug("cleanup");
    for (const u of unsubscribes) {
      try {
        u();
      } catch {}
    }
  };

  // Auto-clean when app broadcasts destroy
  try {
    const unsubscribeAppDestroy = eventBus.on("app:destroy", () => cleanup());
    unsubscribes.push(unsubscribeAppDestroy);
  } catch {}

  return cleanup;
};

// Hydrate selection from session into the store (source: "hydration")
export const hydrateSelectionFromSession = (
  deps: Pick<SelectionAdapterDeps, "store" | "getPath">,
  screen: Record<string, unknown> | null | undefined
): void => {
  if (!screen) return;
  const { store, getPath } = deps;
  const mPath = getPath("selectedMode");
  const stPath = getPath("selectedTool");
  if (!mPath || !stPath) return;
  try {
    store.registerSchema(mPath, zSelectedMode);
  } catch {}
  try {
    store.registerSchema(stPath, zSelectedTool);
  } catch {}
  const eraserModePath = getPath("sketchEraserMode");
  if (eraserModePath) {
    try {
      store.registerSchema(eraserModePath, zSketchEraserMode);
    } catch {}
  }
  const selectionModePath = getPath("sketchSelectionMode");
  if (selectionModePath) {
    try {
      store.registerSchema(selectionModePath, zSketchSelectionMode);
    } catch {}
  }
  try {
    const rawMode =
      typeof (screen as any).selectedMode === "string"
        ? ((screen as any).selectedMode as string)
        : null;
    const rawSubtool = (() => {
      const tool = (screen as any).selectedTool;
      if (typeof tool === "string") return tool;
      const legacy = (screen as any).selected_subtool;
      if (typeof legacy === "string") return legacy;
      return null;
    })();
    const derivedMode = rawMode ?? getModeForSubtool(rawSubtool);
    if (derivedMode) {
      store.set(mPath, derivedMode as any, { source: "hydration" });
    }

    const effectiveMode = derivedMode ?? (store.get?.(mPath) as string | null);
    const normalized = ensureSubtoolForMode(
      (effectiveMode as any) || null,
      rawSubtool
    );
    if (typeof normalized === "string") {
      store.set(stPath, normalized as any, { source: "hydration" });
    } else {
      store.set(stPath, undefined as any, { source: "hydration" });
    }

    const legacySubtoolPath = getPath("selected_subtool");
    if (legacySubtoolPath) {
      store.set(legacySubtoolPath, normalized as any, { source: "hydration" });
    }

    if (eraserModePath) {
      const rawEraser =
        (screen as any).sketchEraserMode ??
        (screen as any).sketch_eraser_mode;
      if (rawEraser === "delete" || rawEraser === "delete-clean") {
        store.set(eraserModePath, rawEraser as any, { source: "hydration" });
      }
    }
    if (selectionModePath) {
      const rawSelection =
        (screen as any).sketchSelectionMode ??
        (screen as any).sketch_selection_mode;
      if (rawSelection === "marquee" || rawSelection === "lasso") {
        store.set(selectionModePath, rawSelection as any, {
          source: "hydration",
        });
      }
    }

    const primaryToolPath = getPath("selected_tool");
    if (primaryToolPath) {
      const desiredTool = resolvePrimaryToolForMode(effectiveMode);
      store.set(primaryToolPath, desiredTool as any, { source: "hydration" });
    }
  } catch {}
};

// Normalize socket patches related to selection into store patch list
export const normalizeSelectionPatch = (
  patch: Record<string, unknown>,
  screenStatePath: (projectId: string, key: string) => string,
  projectId: string
): { path: string; value: unknown }[] => {
  const mapped: Record<string, unknown> = {};
  const mapKey = (k: string): string | null => {
    if (k === "selectedMode") return "selectedMode";
    if (k === "selectedTool" || k === "selected_tool") return "selectedTool";
    if (k === "selectedSubtool" || k === "selected_subtool")
      return "selectedTool";
    if (k === "sketchEraserMode" || k === "sketch_eraser_mode")
      return "sketchEraserMode";
    if (k === "sketchSelectionMode" || k === "sketch_selection_mode")
      return "sketchSelectionMode";
    return null;
  };

  for (const key of Object.keys(patch)) {
    const mappedKey = mapKey(key);
    if (!mappedKey) continue;
    mapped[mappedKey] = (patch as any)[key];
  }

  const mode =
    typeof mapped.selectedMode === "string"
      ? (mapped.selectedMode as string)
      : null;
  const desiredSubtool =
    typeof mapped.selectedTool === "string"
      ? (mapped.selectedTool as string)
      : null;

  let effectiveMode = mode;
  if (!effectiveMode) {
    effectiveMode = getModeForSubtool(desiredSubtool) ?? null;
    if (effectiveMode) mapped.selectedMode = effectiveMode;
  }

  if (effectiveMode) {
    if (typeof desiredSubtool === "string") {
      const normalized = ensureSubtoolForMode(
        effectiveMode as any,
        desiredSubtool
      );
      mapped.selectedTool = normalized ?? null;
      mapped.selected_subtool = normalized ?? null;
    }
    mapped.selected_tool = resolvePrimaryToolForMode(effectiveMode);
  }

  return Object.keys(mapped).map((key) => ({
    path: screenStatePath(projectId, key),
    value: mapped[key],
  }));
};
